import { WEEKDAYS, isBranchOpenNow, normalizeDayShifts, readBranchOperatingHours } from '@/lib/branch-operating-hours';
import { isBranchItemExcluded, resolveMenuItemServiceState } from '@/lib/menu/availability';

// ── Public Table Session Types ──────────────────────────────────────────────

export type PublicPosFeatureFlags = {
  workspace: boolean;
  guestOrdering: boolean;
  counterQr: boolean;
  serviceRequests: boolean;
};

export type PublicResolvedTableSession = {
  sessionType?: 'public' | 'table' | 'counter';
  qrKind?: 'public' | 'table' | 'counter';
  branch: {
    id: string;
    business_id: string;
    name: string;
    slug: string;
    timezone?: string;
    charges?: { taxRate?: number; serviceChargeRate?: number };
  };
  table: {
    id: string;
    branch_id: string;
    zone_id?: string | null;
    label: string;
    order_mode: 'service_console';
    allow_individual_payment?: boolean;
    supports_seat_labels?: boolean;
    service_area_kind?: string | null;
    order_flow?: string | null;
    is_counter_pickup?: boolean;
    qr_kind?: 'table' | 'counter' | null;
  };
  session: {
    id: string;
    session_code: string;
    status: 'active' | 'awaiting_payment' | 'closed' | 'expired' | 'cancelled';
    expires_at: string;
    last_activity_at?: string;
    session_type?: 'table' | 'counter';
    qr_kind?: 'table' | 'counter';
  };
  sessionToken: string;
  seatLabel?: string | null;
};

// ── Table Session Storage ───────────────────────────────────────────────────

function getPublicTableSessionStorageKey(menuId: string, publicCode: string) {
  return `mnrq:table-session:${menuId}:${publicCode}`;
}

function getLatestPublicTableSessionStorageKey(menuId: string) {
  return `mnrq:table-session:${menuId}:latest`;
}

export function readPublicPosFeatureFlags(input: Record<string, unknown>): PublicPosFeatureFlags {
  const flags = input?.branchPosFeatures as PublicPosFeatureFlags | undefined;
  const workspace = Boolean(flags?.workspace ?? input?.branchPosAddonActive);
  return {
    workspace,
    guestOrdering: Boolean(flags?.guestOrdering),
    counterQr: Boolean(flags?.counterQr),
    serviceRequests: Boolean(flags?.serviceRequests),
  };
}

export function isStoredPublicTableSessionUsable(session: PublicResolvedTableSession | null) {
  if (!session) return false;
  const expiresAt = new Date(session.session?.expires_at || '').getTime();
  return Number.isFinite(expiresAt) && expiresAt > Date.now();
}

export function readStoredPublicTableSession(
  menuId: string,
  publicCode: string,
): PublicResolvedTableSession | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(getPublicTableSessionStorageKey(menuId, publicCode));
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as PublicResolvedTableSession;
    if (!isStoredPublicTableSessionUsable(parsed)) {
      window.sessionStorage.removeItem(getPublicTableSessionStorageKey(menuId, publicCode));
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export function readLatestStoredPublicTableSession(menuId: string): PublicResolvedTableSession | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(getLatestPublicTableSessionStorageKey(menuId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { publicCode?: string; session?: PublicResolvedTableSession };
    const session = parsed?.session || null;
    if (!isStoredPublicTableSessionUsable(session)) {
      window.sessionStorage.removeItem(getLatestPublicTableSessionStorageKey(menuId));
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export function writeStoredPublicTableSession(
  menuId: string,
  publicCode: string,
  session: PublicResolvedTableSession,
) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(
      getPublicTableSessionStorageKey(menuId, publicCode),
      JSON.stringify(session),
    );
    window.sessionStorage.setItem(
      getLatestPublicTableSessionStorageKey(menuId),
      JSON.stringify({ publicCode, session }),
    );
  } catch {
    // ignore storage failures in public menu context
  }
}

export function clearStoredPublicTableSession(menuId: string, publicCode: string) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.removeItem(getPublicTableSessionStorageKey(menuId, publicCode));
  } catch {
    // ignore storage failures in public menu context
  }
}

export const TABLE_SCAN_QUERY_KEYS = [
  'qrcode',
  'table',
  'tableCode',
  'table_code',
  'zone_code',
  'seat',
  'seatLabel',
  'chair',
  'counter_pickup',
] as const;

export function cleanResolvedScanUrl() {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  let changed = false;
  for (const key of TABLE_SCAN_QUERY_KEYS) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  }

  if (!changed) return;

  const nextUrl = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, '', nextUrl);
}

// ── Business/Branch Formatting Helpers ──────────────────────────────────────

export function hasBusinessDisplayData(candidate: Record<string, unknown>) {
  if (!candidate || typeof candidate !== 'object') return false;
  return [
    candidate.name,
    candidate.slug,
    candidate.phone,
    candidate.email,
    candidate.address,
    candidate.tagline,
  ].some((value) => typeof value === 'string' && value.trim().length > 0);
}

export function formatClaimExpiry(value?: string | null) {
  if (!value) return 'Valid while this promotion stays active.';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return `Valid until ${value}`;
  }

  return `Valid until ${parsed.toLocaleString()}`;
}

export function formatServiceRequestLabel(value: string) {
  return value.replace(/_/g, ' ');
}

const DAY_LABELS: Record<string, string> = {
  sunday: 'Sunday',
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
};

function formatHour(value?: string) {
  if (!value) return '';
  const [hourRaw, minuteRaw] = value.split(':');
  const hour = Number(hourRaw);
  const minute = Number(minuteRaw || 0);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return value;
  const date = new Date();
  date.setHours(hour, minute, 0, 0);
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function getTodayKey(timezone?: string | null) {
  try {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      timeZone: timezone || 'UTC',
    }).format(new Date()).toLowerCase();
  } catch {
    return WEEKDAYS[new Date().getDay()];
  }
}

function formatShiftWindow(shift: { open: string; close: string }) {
  return `${formatHour(shift.open)}-${formatHour(shift.close)}`;
}

export function formatBranchHoursSummary(rawHours: unknown, timezone?: string | null, openNow?: boolean | null) {
  const hours = readBranchOperatingHours(rawHours);
  const todayKey = getTodayKey(timezone);
  const today = hours[todayKey as keyof typeof hours];
  const todayShifts = normalizeDayShifts(today);
  if (todayShifts.length > 0) {
    return `${openNow ? 'Open now' : 'Today'}: ${todayShifts.map(formatShiftWindow).join(', ')}`;
  }

  const todayIndex = WEEKDAYS.findIndex((day) => day === todayKey);
  const startIndex = todayIndex >= 0 ? todayIndex : 0;
  for (let offset = 1; offset <= WEEKDAYS.length; offset += 1) {
    const day = WEEKDAYS[(startIndex + offset) % WEEKDAYS.length];
    const nextHours = hours[day];
    const nextShifts = normalizeDayShifts(nextHours);
    if (nextShifts.length > 0) {
      return `Next open ${DAY_LABELS[day]}: ${nextShifts.map(formatShiftWindow).join(', ')}`;
    }
  }

  return null;
}

// ── Menu Data Processing ────────────────────────────────────────────────────

export function buildResolvedPublicItems(args: {
  items: any[];
  availability: { rulesByItemId: Map<string, any[]>; exceptionsByItemId: Map<string, any[]> };
  serviceOverrides: Map<string, any>;
  timezone: string;
}) {
  return args.items
    .filter((item) => !isBranchItemExcluded(args.serviceOverrides.get(item.id) || null))
    .map((item) => {
    const serviceState = resolveMenuItemServiceState({
      item,
      override: args.serviceOverrides.get(item.id) || null,
      rules: args.availability.rulesByItemId.get(item.id) || [],
      exceptions: args.availability.exceptionsByItemId.get(item.id) || [],
      timezone: args.timezone,
    });

    return {
      ...item,
      sell_state: serviceState.sellState,
      sell_state_reason: serviceState.reason,
      available_for_order: serviceState.orderable,
      availability_label: serviceState.label || undefined,
      option_groups: Array.isArray(item.option_groups) ? item.option_groups : [],
    };
  });
}

export function deepMergeMenuData(original: any, translated: any): any {
  if (!translated) return original;
  const result = { ...original };
  for (const key of Object.keys(translated)) {
    if (typeof translated[key] === "string" && typeof original[key] === "string") {
      result[key] = translated[key];
    } else if (translated[key] && typeof translated[key] === "object" && !Array.isArray(translated[key])) {
      result[key] = deepMergeMenuData((original[key] as Record<string, unknown>) || {}, translated[key] as Record<string, unknown>);
    } else if (Array.isArray(translated[key]) && Array.isArray(original[key])) {
      const origArr = original[key] as Record<string, unknown>[];
      const transArr = translated[key] as Record<string, unknown>[];
      result[key] = origArr.map((origItem) => {
        if (origItem && typeof origItem === "object") {
          const id = origItem.id ?? origItem.menu_item_id ?? origItem.category_id ?? origItem.collection_id;
          const trans = id ? transArr.find((t) =>
            (t.id ?? t.menu_item_id ?? t.category_id ?? t.collection_id) === id
          ) : undefined;
          return trans ? deepMergeMenuData(origItem, trans) : origItem;
        }
        return origItem;
      });
    } else {
      result[key] = translated[key];
    }
  }
  return result;
}

// ── Customer Mode Persistence ───────────────────────────────────────────────

const CUSTOMER_MODE_KEY_PREFIX = 'menuraq:customer-mode:';

type CustomerModeKey = 'menu' | 'dine_in' | 'pickup' | 'delivery' | 'scheduled';

export function readRememberedCustomerMode(slug: string | null | undefined): CustomerModeKey | null {
  if (typeof window === 'undefined' || !slug) return null;
  try {
    const raw = sessionStorage.getItem(CUSTOMER_MODE_KEY_PREFIX + slug);
    if (raw === 'dine_in' || raw === 'pickup' || raw === 'delivery' || raw === 'scheduled') return raw;
  } catch { /* private mode / storage disabled */ }
  return null;
}

export function rememberCustomerMode(slug: string | null | undefined, mode: CustomerModeKey): void {
  if (typeof window === 'undefined' || !slug) return;
  try {
    if (mode === 'menu') sessionStorage.removeItem(CUSTOMER_MODE_KEY_PREFIX + slug);
    else sessionStorage.setItem(CUSTOMER_MODE_KEY_PREFIX + slug, mode);
  } catch { /* private mode / storage disabled */ }
}
